// M8 Cast Combo — match a sequence of gesture + expression prompts.
// Spec: /microgames/08-cast-combo.md
//
// Pipeline per prompt:
//   1) Show prompt = (gesture, expression).
//   2) Watch every InputFrame. If either hand has the requested gesture AND
//      the face has the requested expression, accumulate hold time.
//   3) When hold time ≥ HOLD_SEC → award point, advance to next prompt.
//   4) If PROMPT_TIME_SEC elapses without a match → no point, advance.
// Win if matched ≥ WIN_THRESHOLD of TOTAL_PROMPTS.

import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
} from '@pose-game/core';

// ─── Tuning ────────────────────────────────────────────────────────────

const DURATION_SEC = 22;
const TOTAL_PROMPTS = 3;
const PROMPT_TIME_SEC = 7; // ~22 / 3, with a tiny buffer
const HOLD_SEC = 0.3;
const WIN_THRESHOLD = 2;

// ─── Domain ────────────────────────────────────────────────────────────

type Gesture = 'open' | 'fist' | 'point' | 'peace';
type Expression = 'smile' | 'neutral' | 'mouthOpen';

interface Prompt {
  gesture: Gesture;
  expression: Expression;
}

const GESTURE_LABEL: Record<Gesture, string> = {
  open: 'OPEN PALM',
  fist: 'FIST',
  point: 'POINT',
  peace: 'PEACE',
};

const GESTURE_ICON: Record<Gesture, string> = {
  open: '✋',
  fist: '✊',
  point: '☝',
  peace: '✌',
};

const EXPRESSION_LABEL: Record<Expression, string> = {
  smile: 'SMILE',
  neutral: 'NEUTRAL',
  mouthOpen: 'MOUTH OPEN',
};

const EXPRESSION_ICON: Record<Expression, string> = {
  smile: '😊',
  neutral: '😐',
  mouthOpen: '😮',
};

const ALL_GESTURES: ReadonlyArray<Gesture> = ['open', 'fist', 'point', 'peace'];
const ALL_EXPRESSIONS: ReadonlyArray<Expression> = [
  'smile',
  'neutral',
  'mouthOpen',
];

// ─── Detection helpers ────────────────────────────────────────────────

function readGesture(frame: InputFrame): Gesture | null {
  const left = frame.hands.left;
  const right = frame.hands.right;
  if (left.visible && left.gesture !== 'unknown') {
    return left.gesture;
  }
  if (right.visible && right.gesture !== 'unknown') {
    return right.gesture;
  }
  return null;
}

function readExpression(frame: InputFrame): Expression | null {
  if (!frame.face.visible) return null;
  // Smile wins over mouthOpen — many smiles open the mouth slightly.
  if (frame.face.smiling) return 'smile';
  if (frame.face.mouthOpen) return 'mouthOpen';
  return 'neutral';
}

// ─── Prompt generation ────────────────────────────────────────────────

function pickPrompt(rng: () => number, prev: Prompt | null): Prompt {
  // Avoid back-to-back identical prompts on both axes.
  for (let attempt = 0; attempt < 8; attempt++) {
    const gIdx = Math.floor(rng() * ALL_GESTURES.length);
    const eIdx = Math.floor(rng() * ALL_EXPRESSIONS.length);
    const gesture =
      ALL_GESTURES[Math.min(gIdx, ALL_GESTURES.length - 1)] ?? 'open';
    const expression =
      ALL_EXPRESSIONS[Math.min(eIdx, ALL_EXPRESSIONS.length - 1)] ?? 'neutral';
    if (
      prev == null ||
      prev.gesture !== gesture ||
      prev.expression !== expression
    ) {
      return { gesture, expression };
    }
  }
  return { gesture: 'open', expression: 'smile' };
}

function buildPrompts(rng: () => number): Prompt[] {
  const out: Prompt[] = [];
  let prev: Prompt | null = null;
  for (let i = 0; i < TOTAL_PROMPTS; i++) {
    const p = pickPrompt(rng, prev);
    out.push(p);
    prev = p;
  }
  return out;
}

// ─── State ────────────────────────────────────────────────────────────

interface CastState {
  ctx: GameContext;
  elapsed: number;
  prompts: Prompt[];
  index: number;
  promptElapsed: number;
  holdSec: number;
  matched: number;
  liveGesture: Gesture | null;
  liveExpression: Expression | null;
  lastResultFlash: 'hit' | 'miss' | null;
  flashTimer: number;
  finished: boolean;
}

function makeState(gameCtx: GameContext): CastState {
  return {
    ctx: gameCtx,
    elapsed: 0,
    prompts: buildPrompts(gameCtx.rng),
    index: 0,
    promptElapsed: 0,
    holdSec: 0,
    matched: 0,
    liveGesture: null,
    liveExpression: null,
    lastResultFlash: null,
    flashTimer: 0,
    finished: false,
  };
}

// ─── Tick ─────────────────────────────────────────────────────────────

function advance(state: CastState, hit: boolean): void {
  if (hit) {
    state.matched += 1;
    state.lastResultFlash = 'hit';
    state.ctx.audio.pop();
  } else {
    state.lastResultFlash = 'miss';
    state.ctx.audio.thud();
  }
  state.flashTimer = 0.5;
  state.index += 1;
  state.promptElapsed = 0;
  state.holdSec = 0;
  if (state.index >= state.prompts.length) {
    state.finished = true;
    if (state.matched >= WIN_THRESHOLD) {
      state.ctx.audio.win();
    } else {
      state.ctx.audio.lose();
    }
  }
}

function tickState(state: CastState, dt: number, frame: InputFrame): void {
  if (state.finished) return;
  const clampedDt = Math.max(0, Math.min(0.1, dt));
  state.elapsed += clampedDt;
  state.promptElapsed += clampedDt;
  if (state.flashTimer > 0) {
    state.flashTimer = Math.max(0, state.flashTimer - clampedDt);
    if (state.flashTimer === 0) state.lastResultFlash = null;
  }

  const current = state.prompts[state.index];
  if (current == null) {
    state.finished = true;
    return;
  }

  const detectedGesture = readGesture(frame);
  const detectedExpression = readExpression(frame);
  state.liveGesture = detectedGesture;
  state.liveExpression = detectedExpression;

  const gestureOk = detectedGesture === current.gesture;
  const expressionOk = detectedExpression === current.expression;
  if (gestureOk && expressionOk) {
    state.holdSec += clampedDt;
    if (state.holdSec >= HOLD_SEC) {
      advance(state, true);
      return;
    }
  } else {
    // Reset hold timer on any mismatch — both must be held simultaneously.
    state.holdSec = 0;
  }

  if (state.promptElapsed >= PROMPT_TIME_SEC) {
    advance(state, false);
    return;
  }

  if (state.elapsed >= DURATION_SEC) {
    state.finished = true;
  }
}

// ─── Render ───────────────────────────────────────────────────────────

function renderState(state: CastState, g: CanvasRenderingContext2D): void {
  const canvas = state.ctx.canvas;
  const w = canvas.width;
  const h = canvas.height;

  // Background.
  g.fillStyle = '#11121a';
  g.fillRect(0, 0, w, h);

  const current = state.prompts[state.index];

  // Centre prompt card.
  const cardW = Math.min(w * 0.72, 720);
  const cardH = Math.min(h * 0.46, 360);
  const cardX = (w - cardW) / 2;
  const cardY = (h - cardH) / 2 - h * 0.02;

  // Flash colour reflects last result so the player gets a beat of feedback.
  let cardFill = '#1c1f2e';
  if (state.lastResultFlash === 'hit' && state.flashTimer > 0) {
    cardFill = '#1f3a26';
  } else if (state.lastResultFlash === 'miss' && state.flashTimer > 0) {
    cardFill = '#3a1f24';
  }
  g.fillStyle = cardFill;
  roundRect(g, cardX, cardY, cardW, cardH, 24);
  g.fill();
  g.lineWidth = 2;
  g.strokeStyle = 'rgba(255,255,255,0.08)';
  g.stroke();

  g.textAlign = 'center';
  g.textBaseline = 'middle';

  if (current != null && !state.finished) {
    // Icon row.
    const iconSize = Math.round(Math.min(cardW * 0.18, cardH * 0.42));
    g.font = `${iconSize}px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif`;
    g.fillStyle = '#fff';
    const iconY = cardY + cardH * 0.35;
    const gestureIconX = cardX + cardW * 0.32;
    const expressionIconX = cardX + cardW * 0.68;
    g.fillText(GESTURE_ICON[current.gesture], gestureIconX, iconY);
    g.fillText(EXPRESSION_ICON[current.expression], expressionIconX, iconY);

    // Plus separator.
    g.font = `bold ${Math.round(iconSize * 0.55)}px system-ui, sans-serif`;
    g.fillStyle = '#7d8298';
    g.fillText('+', (gestureIconX + expressionIconX) / 2, iconY);

    // Label row.
    const labelY = cardY + cardH * 0.72;
    g.font = `bold ${Math.round(Math.max(16, h * 0.028))}px system-ui, sans-serif`;
    g.fillStyle = '#e8ecff';
    g.fillText(GESTURE_LABEL[current.gesture], gestureIconX, labelY);
    g.fillText(EXPRESSION_LABEL[current.expression], expressionIconX, labelY);

    // Hold progress bar below the card.
    const barW = cardW * 0.7;
    const barH = Math.max(10, h * 0.018);
    const barX = (w - barW) / 2;
    const barY = cardY + cardH + h * 0.04;
    g.fillStyle = 'rgba(255,255,255,0.1)';
    roundRect(g, barX, barY, barW, barH, barH / 2);
    g.fill();
    const progress = Math.min(1, state.holdSec / HOLD_SEC);
    if (progress > 0) {
      g.fillStyle = '#5fd17a';
      roundRect(g, barX, barY, barW * progress, barH, barH / 2);
      g.fill();
    }
  } else if (state.finished) {
    const won = state.matched >= WIN_THRESHOLD;
    g.font = `bold ${Math.round(h * 0.06)}px system-ui, sans-serif`;
    g.fillStyle = won ? '#5fd17a' : '#ff7a85';
    g.fillText(won ? 'COMBO COMPLETE' : 'COMBO FAILED', w / 2, cardY + cardH / 2);
  }

  // HUD top-left.
  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.fillStyle = '#fff';
  g.font = `bold ${Math.round(Math.max(14, h * 0.028))}px system-ui, sans-serif`;
  const remain = Math.max(0, DURATION_SEC - state.elapsed);
  const slot = Math.min(state.index + 1, TOTAL_PROMPTS);
  g.fillText(`cast ${slot}/${TOTAL_PROMPTS} — ${remain.toFixed(1)}s`, 16, 14);
  g.font = `${Math.round(Math.max(12, h * 0.024))}px system-ui, sans-serif`;
  g.fillStyle = '#9aa0bf';
  g.fillText(`combos hit: ${state.matched}`, 16, 14 + Math.max(20, h * 0.038));

  // Live detection panel (right side).
  renderLivePanel(state, g, current);
}

function renderLivePanel(
  state: CastState,
  g: CanvasRenderingContext2D,
  current: Prompt | undefined,
): void {
  const canvas = state.ctx.canvas;
  const w = canvas.width;
  const h = canvas.height;
  const panelW = Math.min(220, w * 0.22);
  const panelH = Math.min(140, h * 0.2);
  const panelX = w - panelW - 16;
  const panelY = 16;

  g.fillStyle = 'rgba(255,255,255,0.04)';
  roundRect(g, panelX, panelY, panelW, panelH, 12);
  g.fill();
  g.lineWidth = 1;
  g.strokeStyle = 'rgba(255,255,255,0.08)';
  g.stroke();

  g.textAlign = 'left';
  g.textBaseline = 'top';
  g.font = `bold ${Math.round(Math.max(11, h * 0.018))}px system-ui, sans-serif`;
  g.fillStyle = '#7d8298';
  g.fillText('YOU', panelX + 12, panelY + 10);

  const gestureLabel =
    state.liveGesture != null ? GESTURE_LABEL[state.liveGesture] : '—';
  const expressionLabel =
    state.liveExpression != null
      ? EXPRESSION_LABEL[state.liveExpression]
      : '—';
  const gestureMatches =
    current != null && state.liveGesture === current.gesture;
  const expressionMatches =
    current != null && state.liveExpression === current.expression;

  g.font = `${Math.round(Math.max(14, h * 0.024))}px system-ui, sans-serif`;
  g.fillStyle = gestureMatches ? '#5fd17a' : '#e8ecff';
  g.fillText(`gesture: ${gestureLabel}`, panelX + 12, panelY + 40);
  g.fillStyle = expressionMatches ? '#5fd17a' : '#e8ecff';
  g.fillText(`face: ${expressionLabel}`, panelX + 12, panelY + 70);

  // Tiny indicator dot in the corner.
  const matched = gestureMatches && expressionMatches;
  g.beginPath();
  g.fillStyle = matched ? '#5fd17a' : 'rgba(255,255,255,0.2)';
  g.arc(panelX + panelW - 16, panelY + 18, 6, 0, Math.PI * 2);
  g.fill();
}

function roundRect(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  g.beginPath();
  g.moveTo(x + rr, y);
  g.lineTo(x + w - rr, y);
  g.quadraticCurveTo(x + w, y, x + w, y + rr);
  g.lineTo(x + w, y + h - rr);
  g.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  g.lineTo(x + rr, y + h);
  g.quadraticCurveTo(x, y + h, x, y + h - rr);
  g.lineTo(x, y + rr);
  g.quadraticCurveTo(x, y, x + rr, y);
  g.closePath();
}

// ─── Microgame export ─────────────────────────────────────────────────

let state: CastState | null = null;

export const castCombo: Microgame = {
  name: '08-cast-combo',
  durationSec: DURATION_SEC,
  requires: { hands: true, face: true },
  init(c: GameContext): void {
    state = makeState(c);
  },
  tick(dt: number, frame: InputFrame): void {
    if (state == null) return;
    tickState(state, dt, frame);
  },
  render(g: CanvasRenderingContext2D): void {
    if (state == null) return;
    renderState(state, g);
  },
  end(): MicrogameResult {
    const s = state;
    if (s == null) {
      return {
        microgame: '08-cast-combo',
        won: false,
        score: 0,
        detail: { totalPrompts: TOTAL_PROMPTS },
      };
    }
    const won = s.matched >= WIN_THRESHOLD;
    return {
      microgame: '08-cast-combo',
      won,
      score: s.matched,
      detail: { totalPrompts: TOTAL_PROMPTS },
    };
  },
};
